import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStrategiesStore } from "@/stores/strategiesStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { StrategyWithVersions, StrategyVersion } from "@/lib/types";
import { api } from "@/lib/api";
import { formatTimestamp } from "@/lib/datetime";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import StrategyVersionForm from "@/components/StrategyVersionForm";
import { LANGUAGE_MAP, DEFAULT_LANGUAGE } from "@/pages/Strategies";

const StrategyDetails = () => {
  const { strategyId } = useParams<{ strategyId: string }>();
  const [strategy, setStrategy] = useState<StrategyWithVersions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { updateStrategy } = useStrategiesStore();
  const { selectedOrganizationId } = useOrganizationsStore();

  useEffect(() => {
    const fetchStrategy = async () => {
      if (!strategyId || !selectedOrganizationId) return;
      setIsLoading(true);
      try {
        const fetchedStrategy = await api.getStrategy(strategyId);
        setStrategy(fetchedStrategy);
        updateStrategy(selectedOrganizationId, strategyId, fetchedStrategy);
      } catch (err) {
        setError("Failed to fetch strategy details");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStrategy();
  }, [strategyId, selectedOrganizationId, updateStrategy]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!strategy) return <div>Strategy not found</div>;

  const handleVersionCreated = (newVersion: StrategyVersion) => {
    setStrategy((prev) => {
      if (!prev) return null;
      return { ...prev, versions: [...prev.versions, newVersion] };
    });
    setIsDialogOpen(false);
  };

  const getDetail = (label: string, value: React.ReactNode) => (
    <p>
      <span className="uppercase text-xs font-bold text-muted-foreground/60 mr-2">
        {label}
      </span>
      {value}
    </p>
  );

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <CardTitle>{strategy.name}</CardTitle>
          <CardDescription>{strategy.description}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm flex flex-col gap-3 sm:flex-row sm:justify-between">
          {getDetail("Call Type", <b>{strategy.call_type}</b>)}
          {getDetail(
            "Created At",
            formatTimestamp(strategy.created_at, "MMMM d, yyyy h:mm a")
          )}
          {getDetail(
            "Language",
            <b>{LANGUAGE_MAP[strategy.language ?? DEFAULT_LANGUAGE]}</b>
          )}
        </CardContent>
      </Card>

      <h2 className="text-xl font-bold mt-6 mb-4">Strategy Versions</h2>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button className="mt-2 mb-6">Create New Version</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Create a new version of this strategy.
            </DialogDescription>
          </DialogHeader>
          <StrategyVersionForm
            strategyId={strategy.id}
            onVersionCreated={handleVersionCreated}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      {strategy.versions.map((version) => (
        <Card key={version.id} className="mb-4">
          <CardHeader>
            <CardTitle className="text-md">
              {" "}
              {version.version_description}
            </CardTitle>
            <CardDescription>Version: {version.id}</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="mb-3 flex flex-col">
              <span className="uppercase text-xs font-bold text-muted-foreground/60 mr-2">
                Prompt
              </span>
              <span className="whitespace-pre-wrap">{version.prompt}</span>
              
            </p>
            <p className="mb-3 flex flex-col">
              <span className="uppercase text-xs font-bold text-muted-foreground/60 mr-2">
                Main System Prompt
              </span>
              {version.main_system_prompt.prompt}
            </p>
            {getDetail(
              "LLM Model",
              <span>
                {version.llm_model_provider}{" "}
                <span className="font-bold text-muted-foreground/60 ml-2">
                  {version.llm_model_name}
                </span>
              </span>
            )}
            {getDetail(
              "Created At",
              formatTimestamp(version.created_at, "MMMM d, yyyy h:mm a")
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default StrategyDetails;
